import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Assignment, Visibility } from '@mui/icons-material';
import { apiService } from '../services/api';

type CrewLogin = {
  crewId: string;
  crewNumber: number;
  email: string;
  password?: string | null;
  firstName?: string;
  lastName?: string;
};

const SERVICE_COLUMNS: { key: string; label: string }[] = [
  { key: 'mowing', label: 'Lawn Mowing' },
  { key: 'fertilizing', label: 'Weed Control/Fertilizing' },
  { key: 'snow_removal', label: 'Snow Removal' },
  { key: 'additional_services', label: 'Additional Services' },
];

function normalizeCrewLogins(data: any[]): CrewLogin[] {
  return data.map((crew: any) => {
    const crewNumber = crew.crewNumber || parseInt(String(crew.crewId || '').replace('crew-', '') || '1', 10);
    return {
      crewId: crew.crewId || `crew-${crewNumber}`,
      crewNumber,
      email: crew.email || '',
      password: crew.password ?? crew.crewLoginPassword ?? null,
      firstName: crew.firstName || `Crew ${crewNumber}`,
      lastName: crew.lastName || '',
    };
  });
}

/**
 * Crew setup: View & Edit logins (creates missing), Assign Services.
 * Crew display names come only from logins; team cards link automatically (roster never overwritten).
 */
const CrewSetupPanel: React.FC<{ onCrewsChanged?: () => void }> = ({ onCrewsChanged }) => {
  const [crewCount, setCrewCount] = useState(2);
  const [crewLogins, setCrewLogins] = useState<CrewLogin[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [loginsDialogOpen, setLoginsDialogOpen] = useState(false);
  const [assignmentsDialogOpen, setAssignmentsDialogOpen] = useState(false);
  const [assignments, setAssignments] = useState<Record<number, string[]>>({});
  const [assignmentsError, setAssignmentsError] = useState<string | null>(null);
  const [editingLogin, setEditingLogin] = useState<{ crewId: string; crewName: string; password?: string } | null>(null);
  const [savingCredentials, setSavingCredentials] = useState(false);

  const loadCrewLogins = useCallback(async (): Promise<CrewLogin[]> => {
    const response = await apiService.get('/route-optimization/crew-logins');
    if (response.success && Array.isArray(response.data)) {
      const list = normalizeCrewLogins(response.data);
      setCrewLogins(list);
      if (list.length > 0) {
        const maxNum = Math.max(...list.map((c) => c.crewNumber));
        setCrewCount((prev) => Math.min(6, Math.max(prev, maxNum)));
      }
      return list;
    }
    setCrewLogins([]);
    return [];
  }, []);

  const loadAssignments = useCallback(async () => {
    const response = await apiService.get('/route-optimization/crew-service-assignments');
    if (response.success && response.data) {
      const map: Record<number, string[]> = {};
      const rows = Array.isArray(response.data) ? response.data : (response.data as any).data || [];
      rows.forEach((a: any) => {
        map[a.crewNumber] = a.serviceTypes || [];
      });
      setAssignments(map);
    }
  }, []);

  /** Ensure CrewTeam cards exist/linked for logins. Never clears roster or Lead. */
  const ensureTeamCards = useCallback(async () => {
    await apiService.post('/crew-management/crews/sync', {});
    onCrewsChanged?.();
  }, [onCrewsChanged]);

  useEffect(() => {
    loadCrewLogins().catch(() => undefined);
    loadAssignments().catch(() => undefined);
  }, [loadCrewLogins, loadAssignments]);

  const getCrewName = (crewNumber: number): string => {
    const login = crewLogins.find((c) => c.crewNumber === crewNumber);
    if (login) {
      return `${login.firstName || ''} ${login.lastName || ''}`.trim() || `Crew ${crewNumber}`;
    }
    return `Crew ${crewNumber}`;
  };

  /** Create any missing logins for the selected count, then open credentials dialog. */
  const handleViewAndEditLogins = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const existing = await loadCrewLogins();
      const crews = Array.from({ length: crewCount }, (_, i) => {
        const n = i + 1;
        const found = existing.find((c) => c.crewNumber === n);
        const name = found
          ? `${found.firstName || ''} ${found.lastName || ''}`.trim()
          : `Crew ${n}`;
        return { crewId: `crew-${n}`, crewName: name || `Crew ${n}` };
      });

      const response = await apiService.post('/route-optimization/crew-logins/generate-all', { crews });
      if (!response.success) {
        setError(response.error || 'Failed to create crew logins');
        return;
      }

      await loadCrewLogins();
      await ensureTeamCards();
      setLoginsDialogOpen(true);
      setSuccess(null);
    } catch (e: any) {
      setError(e.message || 'Failed to open crew logins');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAssignments = async () => {
    setAssignmentsError(null);
    await loadCrewLogins();
    await loadAssignments();
    const n = Math.max(crewCount, crewLogins.length, 1);
    setAssignments((prev) => {
      const next = { ...prev };
      for (let i = 1; i <= n; i++) {
        if (!next[i]) next[i] = [];
      }
      return next;
    });
    setAssignmentsDialogOpen(true);
  };

  const handleSaveAssignments = async () => {
    try {
      setAssignmentsError(null);
      const n = Math.max(crewCount, ...Object.keys(assignments).map(Number), crewLogins.length || 1);
      const payload = Array.from({ length: n }, (_, i) => ({
        crewNumber: i + 1,
        serviceTypes: assignments[i + 1] || [],
      }));
      const response = await apiService.post('/route-optimization/crew-service-assignments', {
        assignments: payload,
      });
      if (response.success) {
        setSuccess('Service type assignments saved. Route Optimization will use these for Sort and Optimize.');
        setAssignmentsDialogOpen(false);
      } else {
        setAssignmentsError(response.error || 'Failed to save assignments');
      }
    } catch (e: any) {
      setAssignmentsError(e.message || 'Failed to save assignments');
    }
  };

  const handleSaveCredentials = async (crewId: string, crewName?: string, password?: string) => {
    try {
      setSavingCredentials(true);
      setError(null);
      const normalizedCrewId = crewId.startsWith('crew-') ? crewId : `crew-${crewId.match(/(\d+)/)?.[1] || '1'}`;
      const payload: Record<string, string> = {};
      if (crewName?.trim()) payload.crewName = crewName.trim();
      if (password?.trim()) payload.password = password.trim();
      if (Object.keys(payload).length === 0) {
        setError('Enter a crew name or password to update.');
        return;
      }
      const response = await apiService.post(
        `/route-optimization/crew-logins/${normalizedCrewId}/update-credentials`,
        payload
      );
      if (response.success) {
        await loadCrewLogins();
        await ensureTeamCards();
        setSuccess(`Saved credentials for ${normalizedCrewId}.`);
        setEditingLogin(null);
      } else {
        setError(response.error || 'Failed to save credentials');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to save credentials');
    } finally {
      setSavingCredentials(false);
    }
  };

  const assignmentCrewCount = Math.max(
    crewCount,
    crewLogins.length,
    ...Object.keys(assignments).map(Number),
    1
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Crew logins & service types
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Set crew count, create/view logins, and assign which services each crew handles. These stick across days.
        Route Optimization uses them for Sort and Optimize.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Number of crews</InputLabel>
          <Select
            value={crewCount}
            label="Number of crews"
            onChange={(e) => setCrewCount(Number(e.target.value))}
          >
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <MenuItem key={n} value={n}>
                {n} crew{n > 1 ? 's' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Visibility />}
          onClick={handleViewAndEditLogins}
          disabled={loading}
        >
          View &amp; Edit Crew Logins
        </Button>
        <Button variant="outlined" startIcon={<Assignment />} onClick={handleOpenAssignments}>
          Assign Services
        </Button>
      </Stack>

      <Dialog
        open={loginsDialogOpen}
        onClose={() => {
          setLoginsDialogOpen(false);
          setEditingLogin(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Crew Login Credentials</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Crew name is the display name for this login (and the crew card). Username is what they type to sign in.
            Use Edit to change the name or password.
          </Alert>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : crewLogins.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No crew logins yet. Choose a crew count and click View &amp; Edit Crew Logins.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Crew</strong></TableCell>
                    <TableCell><strong>Username</strong></TableCell>
                    <TableCell><strong>Password</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {crewLogins.map((login) => {
                    const crewId = login.crewId;
                    const displayName =
                      `${login.firstName || ''} ${login.lastName || ''}`.trim() || `Crew ${login.crewNumber}`;
                    const isEditing = editingLogin?.crewId === crewId;
                    return (
                      <TableRow key={crewId}>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              fullWidth
                              label="Crew name"
                              value={editingLogin?.crewName ?? ''}
                              onChange={(e) =>
                                setEditingLogin((prev) =>
                                  prev ? { ...prev, crewName: e.target.value } : prev
                                )
                              }
                            />
                          ) : (
                            displayName
                          )}
                        </TableCell>
                        <TableCell>{login.email}</TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              fullWidth
                              label="New password"
                              type="text"
                              value={editingLogin?.password ?? ''}
                              onChange={(e) =>
                                setEditingLogin((prev) =>
                                  prev ? { ...prev, password: e.target.value } : prev
                                )
                              }
                              placeholder="Leave blank to keep"
                            />
                          ) : (
                            login.password || '••••••••'
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Stack direction="row" spacing={1}>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={savingCredentials}
                                onClick={() =>
                                  handleSaveCredentials(
                                    crewId,
                                    editingLogin?.crewName,
                                    editingLogin?.password
                                  )
                                }
                              >
                                Save
                              </Button>
                              <Button size="small" onClick={() => setEditingLogin(null)}>
                                Cancel
                              </Button>
                            </Stack>
                          ) : (
                            <Button
                              size="small"
                              onClick={() =>
                                setEditingLogin({ crewId, crewName: displayName, password: '' })
                              }
                            >
                              Edit
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setLoginsDialogOpen(false);
              setEditingLogin(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assignmentsDialogOpen}
        onClose={() => {
          setAssignmentsDialogOpen(false);
          setAssignmentsError(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Assign Services to Crews
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Select which service types each crew handles. Multiple crews can share a type. These preferences persist
            across days.
          </Typography>
        </DialogTitle>
        <DialogContent>
          {assignmentsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {assignmentsError}
            </Alert>
          )}
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Crew</strong></TableCell>
                  {SERVICE_COLUMNS.map((col) => (
                    <TableCell key={col.key} align="center">
                      <strong>{col.label}</strong>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: assignmentCrewCount }, (_, i) => i + 1).map((crewNum) => (
                  <TableRow key={crewNum}>
                    <TableCell>
                      <strong>{getCrewName(crewNum)}</strong>
                    </TableCell>
                    {SERVICE_COLUMNS.map((col) => (
                      <TableCell key={col.key} align="center">
                        <Checkbox
                          checked={assignments[crewNum]?.includes(col.key) || false}
                          onChange={(e) => {
                            const current = assignments[crewNum] || [];
                            const updated = e.target.checked
                              ? [...current, col.key]
                              : current.filter((t) => t !== col.key);
                            setAssignments({ ...assignments, [crewNum]: updated });
                          }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAssignmentsDialogOpen(false);
              setAssignmentsError(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveAssignments}>
            Save Assignments
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CrewSetupPanel;
